import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, Typography, Button, TextField, IconButton, Grid, MenuItem,
  CircularProgress, Alert, Divider, InputAdornment, Stack, FormControlLabel,
  Switch, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Chip,
} from '@mui/material';
import {
  Close, Payments as PaymentsIcon, Save, SwapHoriz, Edit, Delete,
  InboxOutlined, RestartAlt, LockOutlined, Savings, AccountBalance,
  Add, DeleteOutline, ReceiptLong,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  expenseTypesURL, paymentVouchersURL,
  paymentVouchersAddURL, paymentVouchersAddBatchURL,
  paymentVouchersUpdateURL, paymentVouchersDeleteURL,
} from '../BaseURL';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });
const swalTop = { customClass: { container: 'swal-on-top' } };
const fmt = (v) => Number(v || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 });
const shortDate = (d) => new Date(d).toLocaleDateString('en-GH', { day: '2-digit', month: 'short' });

// Special sentinel used inside form state when the user picks "Cash in Hand"
// as the payment source. Translated to payment_mode='cash' + null account_id
// at save time.
const CASH_ID = 'CASH';

// Amounts are compared with a half-pesewa tolerance so float noise never
// blocks a batch that is actually balanced.
const EPS = 0.005;

const emptyForm = (dateDefault) => ({
  id: null, pv_number: '',
  payment_date: dateDefault,
  description: '', cheque_no: '', amount: '', request_holder: '',
  account_id: '', expense_category: '', expense_type: '',
  is_bank_to_bank: false, target_account_id: '',
});

// Stable keys for the batch line rows (index is not safe — lines get removed).
let lineSeq = 0;
const emptyLine = (dateDefault, amount = '') => ({
  key: `L${++lineSeq}`,
  payment_date: dateDefault,
  amount: String(amount ?? ''),
  expense_category: '', expense_type: '',
  request_holder: '', description: '',
});

export default function RecordPaymentDrawer({
  open, onClose, monthYear, periodFirstDay, periodLastDay,
  accounts = [], bankBalances = [], cashInHand = 0, onSaved,
  initialEntry = null,
}) {
  const [expTypes, setExpTypes]   = useState([]);
  const [payments, setPayments]   = useState([]);
  const [form, setForm]           = useState(emptyForm(periodFirstDay));
  const [isBatch, setIsBatch]     = useState(false);
  const [lines, setLines]         = useState([]);
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const isEditing = form.id != null;

  const loadPayments = useCallback(() => {
    setLoading(true);
    axios.get(`${paymentVouchersURL}?month_year=${monthYear}`, { headers: headers() })
      .then(r => setPayments(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [monthYear]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setIsBatch(false);
    setLines([]);
    if (initialEntry) {
      const isCashMode = initialEntry.payment_mode === 'cash';
      setForm({
        id: initialEntry.id,
        pv_number: initialEntry.pv_number || '',
        payment_date: initialEntry.payment_date?.slice(0, 10) || periodFirstDay,
        description: initialEntry.description || '',
        cheque_no: initialEntry.cheque_no || '',
        amount: String(initialEntry.amount ?? ''),
        request_holder: initialEntry.request_holder || '',
        account_id: isCashMode ? CASH_ID : (initialEntry.account_id || ''),
        expense_category: initialEntry.expense_category || '',
        expense_type: initialEntry.expense_type || '',
        is_bank_to_bank: !!initialEntry.is_bank_to_bank,
        target_account_id: initialEntry.target_account_id || '',
      });
    } else {
      setForm(emptyForm(periodFirstDay));
    }
    axios.get(expenseTypesURL, { headers: headers() })
      .then(r => { if (r.data.success) setExpTypes(r.data.data); })
      .catch(() => {});
    loadPayments();
  }, [open, periodFirstDay, loadPayments, initialEntry]);

  const categories  = [...new Set(expTypes.map(t => t.category))].filter(Boolean);
  const typesInCat  = expTypes.filter(t => !form.expense_category || t.category === form.expense_category);
  const typesFor    = (cat) => expTypes.filter(t => !cat || t.category === cat);
  const balanceOf   = (id) => bankBalances.find(b => b.id === Number(id))?.balance;

  // True when the user picked "Cash in Hand" as the payment source.
  const isCashMode = form.account_id === CASH_ID;

  // For overdraft check: when editing, add back this row's own amount so we don't double-count.
  const currentRowAmt = isEditing
    ? Number(payments.find(p => p.id === form.id)?.amount || 0)
    : 0;

  const effectiveBal = (id) => {
    if (id === CASH_ID) {
      // Cash-mode edits: if the row being edited is itself a cash payment, add it back.
      const wasCash = isEditing && payments.find(p => p.id === form.id)?.payment_mode === 'cash';
      return Number(cashInHand) + (wasCash ? currentRowAmt : 0);
    }
    const b = balanceOf(id);
    if (b == null) return null;
    return Number(b) + (Number(form.account_id) === Number(id) ? currentRowAmt : 0);
  };
  const currentAccBal = effectiveBal(form.account_id);

  // ─── Batch line helpers ────────────────────────────────────────────────────
  const allocated = lines.reduce((s, L) => s + Number(L.amount || 0), 0);
  const remaining = Number(form.amount || 0) - allocated;
  const balanced  = Math.abs(remaining) <= EPS && lines.length > 0;

  const addLine = () => {
    // Pre-fill the new line with whatever is still unallocated — the common case
    // is a cheque split into a couple of chunks, so this saves typing.
    const seed = remaining > EPS ? remaining.toFixed(2) : '';
    setLines(ls => [...ls, emptyLine(form.payment_date, seed)]);
  };
  const removeLine = (key) => setLines(ls => ls.filter(L => L.key !== key));
  const patchLine  = (key, patch) =>
    setLines(ls => ls.map(L => (L.key === key ? { ...L, ...patch } : L)));

  const toggleBatch = (on) => {
    setError('');
    setIsBatch(on);
    setLines(on ? [emptyLine(form.payment_date, form.amount)] : []);
  };

  // ─── Validation ────────────────────────────────────────────────────────────
  const validateShared = () => {
    if (!form.account_id) return 'Select a payment source.';
    if (!form.amount || Number(form.amount) <= 0) return 'Enter a valid amount.';
    if (currentAccBal != null && Number(form.amount) > Number(currentAccBal)) {
      return isCashMode
        ? `Amount exceeds cash in hand (GH₵ ${fmt(currentAccBal)}).`
        : `Amount exceeds available balance (GH₵ ${fmt(currentAccBal)}).`;
    }
    return null;
  };

  const inPeriod = (d) => !periodFirstDay || (d >= periodFirstDay && d <= periodLastDay);

  const validateSingle = () => {
    if (!form.payment_date) return 'Payment date is required.';
    if (!inPeriod(form.payment_date)) return `Date must be within ${monthYear}.`;
    const shared = validateShared();
    if (shared) return shared;
    if (form.is_bank_to_bank) {
      if (!form.target_account_id) return 'Select the target account.';
      if (Number(form.target_account_id) === Number(form.account_id)) {
        return 'Target account must differ from the source.';
      }
    } else if (!form.expense_category || !form.expense_type) {
      return 'Select expense category and type.';
    }
    if (!form.request_holder.trim()) return 'Request holder is required.';
    if (!form.description.trim())    return 'Description is required.';
    // Cheque number is only required for bank payments (not cash, not BTB — BTB uses transfer).
    if (!isCashMode && !form.is_bank_to_bank && !form.cheque_no.trim()) {
      return 'Cheque number is required for bank payments.';
    }
    return null;
  };

  const validateBatch = () => {
    const shared = validateShared();
    if (shared) return shared;
    if (!isCashMode && !form.cheque_no.trim()) {
      return 'Cheque number is required for bank payments.';
    }
    if (lines.length === 0) return 'Add at least one payment line.';
    for (let i = 0; i < lines.length; i++) {
      const L = lines[i], n = `Line ${i + 1}: `;
      if (!L.payment_date)                    return `${n}date is required.`;
      if (!inPeriod(L.payment_date))          return `${n}date must be within ${monthYear}.`;
      if (!L.amount || Number(L.amount) <= 0) return `${n}enter a valid amount.`;
      if (!L.expense_category || !L.expense_type) return `${n}select expense category and type.`;
      if (!L.request_holder.trim())           return `${n}request holder is required.`;
      if (!L.description.trim())              return `${n}description is required.`;
    }
    if (!balanced) {
      return remaining > 0
        ? `GH₵ ${fmt(remaining)} of the payment is still unallocated — the lines must add up to GH₵ ${fmt(form.amount)}.`
        : `The lines total GH₵ ${fmt(allocated)}, which is GH₵ ${fmt(-remaining)} more than the payment amount.`;
    }
    return null;
  };

  const resetForm = () => {
    setForm(emptyForm(periodFirstDay));
    setIsBatch(false);
    setLines([]);
    setError('');
  };

  const saveBatch = async () => {
    const acc = accounts.find(a => a.id === Number(form.account_id));
    const payload = {
      cheque_no: isCashMode ? null : form.cheque_no,
      payment_mode: isCashMode ? 'cash' : 'bank',
      account_id: isCashMode ? null : form.account_id,
      account_name: isCashMode ? 'CASH' : (acc?.name || ''),
      total_amount: Number(form.amount),
      lines: lines.map(({ key, ...L }) => L),
    };
    const r = await axios.post(paymentVouchersAddBatchURL, payload, { headers: headers() });
    Swal.fire({
      ...swalTop, icon: 'success',
      title: `${r.data.count} payments saved`,
      text: isCashMode
        ? `GH₵ ${fmt(form.amount)} paid in cash`
        : `Cheque ${form.cheque_no} — GH₵ ${fmt(form.amount)}`,
      timer: 1800, showConfirmButton: false,
    });
  };

  const saveSingle = async () => {
    // Translate cash-mode form values back to the server contract.
    const acc = accounts.find(a => a.id === Number(form.account_id));
    const payload = {
      ...form,
      payment_mode: isCashMode ? 'cash' : 'bank',
      account_id: isCashMode ? null : form.account_id,
      account_name: isCashMode ? 'CASH' : (acc?.name || ''),
      cheque_no: isCashMode ? null : form.cheque_no,
    };
    const url = isEditing ? paymentVouchersUpdateURL : paymentVouchersAddURL;
    const r = await axios.post(url, payload, { headers: headers() });
    Swal.fire({
      ...swalTop, icon: 'success',
      title: isEditing
        ? 'Payment updated'
        : (form.is_bank_to_bank ? 'Bank transfer saved' : 'Payment saved'),
      text: isEditing
        ? `GH₵ ${fmt(form.amount)}`
        : (r.data.pv_number ? `PV: ${r.data.pv_number}` : ''),
      timer: 1400, showConfirmButton: false,
    });
  };

  const save = async () => {
    const err = isBatch ? validateBatch() : validateSingle();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    try {
      if (isBatch) await saveBatch();
      else         await saveSingle();
      resetForm();
      loadPayments();
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save payment.');
    } finally {
      setSaving(false);
    }
  };

  const editPayment = (p) => {
    setError('');
    setIsBatch(false);
    setLines([]);
    setForm({
      id: p.id,
      pv_number: p.pv_number || '',
      payment_date: p.payment_date?.slice(0, 10) || periodFirstDay,
      description: p.description || '',
      cheque_no: p.cheque_no || '',
      amount: String(p.amount ?? ''),
      request_holder: p.request_holder || '',
      account_id: p.payment_mode === 'cash' ? CASH_ID : (p.account_id || ''),
      expense_category: p.expense_category || '',
      expense_type: p.expense_type || '',
      is_bank_to_bank: !!p.is_bank_to_bank,
      target_account_id: p.target_account_id || '',
    });
    document.querySelector('#payment-drawer-body')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deletePayment = (p) => {
    Swal.fire({
      ...swalTop, title: `Delete ${p.pv_number}?`,
      text: `${p.expense_type || (p.is_bank_to_bank ? 'Bank Transfer' : '')} — GH₵ ${fmt(p.amount)}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#B91C1C', confirmButtonText: 'Delete',
    }).then(async res => {
      if (!res.isConfirmed) return;
      try {
        await axios.post(paymentVouchersDeleteURL, { id: p.id }, { headers: headers() });
        if (form.id === p.id) resetForm();
        loadPayments();
        onSaved?.();
      } catch (e) {
        Swal.fire({ ...swalTop, icon: 'error', title: 'Delete failed', text: e.response?.data?.message || 'Try again.' });
      }
    });
  };

  const monthTotal = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  const headerTitle = isEditing
    ? (form.is_bank_to_bank ? 'Edit Bank Transfer' : 'Edit Payment')
    : isBatch
      ? 'Batch Payment'
      : (form.is_bank_to_bank ? 'Bank-to-Bank Transfer' : 'Record Payment');

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: isBatch ? 780 : 620 }, borderRadius: 0 } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', px: 3, py: 2.5,
          flexShrink: 0,
        }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, bgcolor: 'secondary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(200,146,17,0.35)',
          }}>
            {form.is_bank_to_bank ? <SwapHoriz /> : isBatch ? <ReceiptLong /> : <PaymentsIcon />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {headerTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {monthYear} · {payments.length} voucher{payments.length === 1 ? '' : 's'} · GH₵ {fmt(monthTotal)}
              {isEditing && form.pv_number && ` · ${form.pv_number.split('/').slice(-1)[0]}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>

        {/* Scrollable body */}
        <Box id="payment-drawer-body" sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Form */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
              <Paper variant="outlined" sx={{
                p: 1.5, borderColor: '#E2E8F0',
                bgcolor: form.is_bank_to_bank ? 'rgba(200,146,17,0.06)' : '#FAFBFC',
              }}>
                <FormControlLabel
                  sx={{ m: 0, width: '100%' }}
                  control={
                    <Switch
                      checked={form.is_bank_to_bank}
                      disabled={isEditing || isCashMode || isBatch}
                      onChange={e => setForm(f => ({
                        ...f, is_bank_to_bank: e.target.checked,
                        expense_category: '', expense_type: '', target_account_id: '',
                        // BTB requires a bank source; clear cash pick if it was selected.
                        account_id: e.target.checked && f.account_id === CASH_ID ? '' : f.account_id,
                      }))}
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" fontWeight={600}>Bank-to-Bank Transfer</Typography>
                        {(isEditing || isCashMode || isBatch) && <LockOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {isEditing
                          ? 'Mode is locked while editing an existing voucher.'
                          : isBatch
                            ? 'Not available in batch mode.'
                            : isCashMode
                              ? 'Not available for cash payments.'
                              : 'Move money between two bank accounts (not an expense).'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Paper variant="outlined" sx={{
                p: 1.5, borderColor: isBatch ? 'secondary.main' : '#E2E8F0',
                bgcolor: isBatch ? 'rgba(200,146,17,0.06)' : '#FAFBFC',
              }}>
                <FormControlLabel
                  sx={{ m: 0, width: '100%' }}
                  control={
                    <Switch
                      checked={isBatch}
                      disabled={isEditing || form.is_bank_to_bank}
                      onChange={e => toggleBatch(e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" fontWeight={600}>Batch Payment (split one cheque)</Typography>
                        {(isEditing || form.is_bank_to_bank) && <LockOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {isEditing
                          ? 'Not available while editing an existing voucher.'
                          : form.is_bank_to_bank
                            ? 'Not available for bank-to-bank transfers.'
                            : 'One cheque and one account paying several expenses — each line becomes its own voucher.'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Stack>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                {isEditing ? 'Editing voucher' : isBatch ? 'Cheque / Withdrawal Details' : 'New Payment'}
              </Typography>
              {isEditing && (
                <Button size="small" startIcon={<RestartAlt />} onClick={resetForm}>
                  New payment
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small"
                  label={isBatch ? 'Default Date' : 'Date'}
                  type="date" required
                  value={form.payment_date}
                  inputProps={{ min: periodFirstDay, max: periodLastDay }}
                  onChange={e => setForm({ ...form, payment_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText={isBatch ? 'Used for new lines — each line can differ' : ' '}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small"
                  label={isBatch ? 'Total Amount' : 'Amount'}
                  type="number" required
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                    sx: { fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
                  }}
                  helperText={isBatch ? 'The full amount leaving the account' : ' '}
                />
              </Grid>

              <Grid item xs={isBatch ? 6 : 12}>
                <TextField
                  select fullWidth size="small"
                  label={form.is_bank_to_bank ? 'Source Account' : 'Payment From'} required
                  value={form.account_id}
                  onChange={e => setForm({ ...form, account_id: e.target.value })}
                  helperText={currentAccBal != null ? `Available: GH₵ ${fmt(currentAccBal)}` : ' '}
                >
                  <MenuItem value=""><em>— Select source —</em></MenuItem>
                  {!form.is_bank_to_bank && (
                    <MenuItem value={CASH_ID}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Savings sx={{ fontSize: 16, color: 'secondary.main' }} />
                        <span>Cash in Hand — GH₵ {fmt(effectiveBal(CASH_ID))}</span>
                      </Box>
                    </MenuItem>
                  )}
                  {accounts.map(a => {
                    const bal = effectiveBal(a.id);
                    return (
                      <MenuItem key={a.id} value={a.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccountBalance sx={{ fontSize: 16, color: 'primary.main' }} />
                          <span>{a.name}{bal != null ? ` — GH₵ ${fmt(bal)}` : ''}</span>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              {/* In batch mode the cheque number is shared by every line, so it
                  belongs up here with the withdrawal details. */}
              {isBatch && (
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small" label="Cheque No"
                    required={!isCashMode}
                    disabled={isCashMode}
                    placeholder={isCashMode ? 'Not needed for cash' : 'One cheque for all lines'}
                    value={isCashMode ? '' : form.cheque_no}
                    onChange={e => setForm({ ...form, cheque_no: e.target.value })}
                    helperText={isCashMode ? 'Not required for cash payments' : 'Shared by every line below'}
                  />
                </Grid>
              )}

              {form.is_bank_to_bank ? (
                <Grid item xs={12}>
                  <TextField
                    select fullWidth size="small" label="Transfer To Account" required
                    value={form.target_account_id}
                    onChange={e => setForm({ ...form, target_account_id: e.target.value })}
                  >
                    <MenuItem value=""><em>— Select target —</em></MenuItem>
                    {accounts.filter(a => a.id !== Number(form.account_id)).map(a => (
                      <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : !isBatch && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      select fullWidth size="small" label="Expense Category" required
                      value={form.expense_category}
                      onChange={e => setForm({ ...form, expense_category: e.target.value, expense_type: '' })}
                    >
                      <MenuItem value=""><em>— Select —</em></MenuItem>
                      {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select fullWidth size="small" label="Expense Type" required
                      value={form.expense_type}
                      onChange={e => setForm({ ...form, expense_type: e.target.value })}
                      disabled={!form.expense_category}
                    >
                      <MenuItem value=""><em>— Select —</em></MenuItem>
                      {typesInCat.map(t => <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>)}
                    </TextField>
                  </Grid>
                </>
              )}

              {!isBatch && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small" label="Request Holder" required
                      placeholder="Who requested this?"
                      value={form.request_holder}
                      onChange={e => setForm({ ...form, request_holder: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth size="small"
                      label="Cheque No"
                      required={!isCashMode && !form.is_bank_to_bank}
                      disabled={isCashMode}
                      placeholder={isCashMode ? 'Not needed for cash' : 'Cheque number'}
                      value={isCashMode ? '' : form.cheque_no}
                      onChange={e => setForm({ ...form, cheque_no: e.target.value })}
                      helperText={isCashMode ? 'Not required for cash payments' : ' '}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" label="Description" required
                      placeholder="What is this payment for?"
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      multiline minRows={2}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* ─── Batch lines ─────────────────────────────────────────────── */}
            {isBatch && (
              <Box sx={{ mt: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                    Payment Lines ({lines.length})
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<Add />} onClick={addLine}>
                    Add line
                  </Button>
                </Box>

                {/* Allocation meter — the lines must add up to the total above. */}
                <Paper variant="outlined" sx={{
                  px: 1.75, py: 1.25, mb: 1.75, borderRadius: 2,
                  borderColor: balanced ? 'success.main' : remaining < -EPS ? 'error.main' : '#E2E8F0',
                  bgcolor: balanced ? 'rgba(22,163,74,0.06)' : remaining < -EPS ? 'rgba(185,28,28,0.05)' : '#F8FAFC',
                }}>
                  <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Allocated</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        GH₵ {fmt(allocated)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        GH₵ {fmt(form.amount)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {remaining < -EPS ? 'Over by' : 'Unallocated'}
                      </Typography>
                      <Typography
                        variant="body2" fontWeight={800}
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          color: balanced ? 'success.main' : remaining < -EPS ? 'error.main' : 'warning.main',
                        }}
                      >
                        {balanced ? 'Balanced ✓' : `GH₵ ${fmt(Math.abs(remaining))}`}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {lines.length === 0 ? (
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    py: 3, color: 'text.secondary', textAlign: 'center',
                    border: '1px dashed #CBD5E1', borderRadius: 2,
                  }}>
                    <InboxOutlined sx={{ fontSize: 32, color: '#CBD5E1', mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600}>No lines yet</Typography>
                    <Typography variant="caption" color="text.disabled">
                      Click "Add line" for each expense this cheque covers.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {lines.map((L, i) => (
                      <Paper key={L.key} variant="outlined" sx={{ p: 1.75, borderRadius: 2, borderColor: '#E2E8F0' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.25}>
                          <Chip
                            size="small" label={`Line ${i + 1}`}
                            sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: '#EEF2F7' }}
                          />
                          <Tooltip title="Remove line">
                            <span>
                              <IconButton
                                size="small" color="error" sx={{ p: 0.3 }}
                                disabled={lines.length === 1}
                                onClick={() => removeLine(L.key)}
                              >
                                <DeleteOutline sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth size="small" label="Date" type="date" required
                              value={L.payment_date}
                              inputProps={{ min: periodFirstDay, max: periodLastDay }}
                              onChange={e => patchLine(L.key, { payment_date: e.target.value })}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              fullWidth size="small" label="Amount" type="number" required
                              value={L.amount}
                              onChange={e => patchLine(L.key, { amount: e.target.value })}
                              inputProps={{ min: 0, step: '0.01' }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                                sx: { fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small" label="Request Holder" required
                              placeholder="Who requested this?"
                              value={L.request_holder}
                              onChange={e => patchLine(L.key, { request_holder: e.target.value })}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <TextField
                              select fullWidth size="small" label="Expense Category" required
                              value={L.expense_category}
                              onChange={e => patchLine(L.key, { expense_category: e.target.value, expense_type: '' })}
                            >
                              <MenuItem value=""><em>— Select —</em></MenuItem>
                              {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              select fullWidth size="small" label="Expense Type" required
                              value={L.expense_type}
                              onChange={e => patchLine(L.key, { expense_type: e.target.value })}
                              disabled={!L.expense_category}
                            >
                              <MenuItem value=""><em>— Select —</em></MenuItem>
                              {typesFor(L.expense_category).map(t => (
                                <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth size="small" label="Description" required
                              placeholder="What is this line for?"
                              value={L.description}
                              onChange={e => patchLine(L.key, { description: e.target.value })}
                              multiline minRows={2}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          {/* Sub-header */}
          <Box sx={{
            px: 2, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
            borderBottom: '1px solid #E2E8F0',
          }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10 }}>
              This Month's Vouchers
            </Typography>
            <Typography variant="caption" fontWeight={700} color="text.disabled"
              sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
              {payments.length}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} /></Box>
          ) : payments.length === 0 ? (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              px: 3, py: 4, color: 'text.secondary', textAlign: 'center',
            }}>
              <InboxOutlined sx={{ fontSize: 36, color: '#CBD5E1', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>No payments yet for {monthYear}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                Fill the form above and click Save.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>Date</TableCell>
                    <TableCell width={70}>PV #</TableCell>
                    <TableCell>Type / Account</TableCell>
                    <TableCell align="right">Amount (GH₵)</TableCell>
                    <TableCell align="right" width={80}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map(p => {
                    const active = form.id === p.id;
                    const btb = !!p.is_bank_to_bank;
                    return (
                      <TableRow key={p.id} hover selected={active}
                        sx={{
                          bgcolor: active ? 'rgba(200,146,17,0.08) !important' : undefined,
                          '& td:first-of-type': {
                            borderLeft: active ? '3px solid' : '3px solid transparent',
                            borderColor: active ? 'secondary.main' : 'transparent',
                          },
                        }}>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          {shortDate(p.payment_date)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                          {(p.pv_number || '').split('/').slice(-1)[0]}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {p.expense_type || (btb ? 'Bank Transfer' : '—')}
                            </Typography>
                            {btb && (
                              <Chip size="small" label="BTB" variant="outlined" color="warning"
                                sx={{ height: 16, fontSize: 9, fontWeight: 700 }} />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {p.account_name || '—'}
                            {p.cheque_no && ` · Chq ${p.cheque_no}`}
                            {btb && p.target_account_id && (
                              <> → {accounts.find(a => a.id === p.target_account_id)?.name || '—'}</>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 700, color: 'error.main',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmt(p.amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => editPayment(p)} sx={{ p: 0.3 }}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deletePayment(p)} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, color: 'error.main',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmt(monthTotal)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Stack direction="row" spacing={1.5} alignItems="center"
          sx={{ px: 3, py: 2, bgcolor: '#FAFBFC', flexShrink: 0 }} justifyContent="flex-end">
          {isBatch && !balanced && lines.length > 0 && (
            <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ mr: 'auto' }}>
              Lines must add up to GH₵ {fmt(form.amount)}
            </Typography>
          )}
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained" onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ px: 3 }}
          >
            {isEditing
              ? 'Save Changes'
              : isBatch
                ? `Save ${lines.length} Payment${lines.length === 1 ? '' : 's'}`
                : (form.is_bank_to_bank ? 'Save Transfer' : 'Save Payment')}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
